import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import type { ReactNode } from 'react';

import { Slider } from '@hivebrite/design-system/Slider';

import { networkId } from 'hb-react/backoffice/apps/Branding/__mocks__';
import { GroupThumbnail } from 'hb-react/frontoffice/components/GroupThumbnail';
import GroupLoadingCard from 'hb-react/frontoffice/components/GroupThumbnail/GroupLoadingCard';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import type { RelatedGroup } from 'hb-react/shared/apps/Events/components/EventDetailsContainer/types';
import { axiosInstance } from 'hb-react/shared/utils/axios';

interface EventRelatedGroupsProps {
  eventId: number;
  wrapper: ({ children }: { children: ReactNode }) => JSX.Element;
  title: () => JSX.Element;
}

const EventRelatedGroups = ({
  eventId,
  wrapper: Wrapper,
  title: Title,
}: EventRelatedGroupsProps) => {
  const { topicId } = useTopicContext();
  const path = topicId
    ? Routes.topic_network_event_related_topics_path(topicId, eventId, {
        format: 'json',
      })
    : Routes.related_topics_event_path(eventId, { format: 'json' });

  const { data = [], isLoading } = useQuery<RelatedGroup[], AxiosError>({
    queryKey: ['event-related-groups', networkId, topicId],
    queryFn: () =>
      axiosInstance
        .get<{ topics: RelatedGroup[] }>(path)
        .then(({ data }) => data.topics),
  });

  if (!isLoading && data.length === 0) {
    return null;
  }

  return (
    <Wrapper>
      <Title />
      <Slider
        items={data}
        ItemComponent={GroupThumbnail}
        perPageBreakpoints={{
          s: 1,
          m: 2,
          l: 3,
          xl: 3,
        }}
        infinite={false}
        loading={isLoading}
        LoadingItemComponent={GroupLoadingCard}
        total={data.length}
      />
    </Wrapper>
  );
};

export default EventRelatedGroups;
